export * from './enums';
export * from './types';

import { ICampaignGroup } from '@/types/campaign/groups';
import { AudienceStatistics } from '@/types/audience/audience';
import { TranslateResult } from 'vue-i18n';
import { IDictionaryList } from '@/types/dictionary';
import { ICreativeTemplate, IShowcase } from '@/types/campaign/creatives';

export interface ResponseData {
  success: boolean;
  data: any;
  ssp_user_id_count?: number | string | null;
}

export interface ResponseError {
  response: ResponseData;
}

export interface ResponseCodeData {
  success: boolean;
  data: any;
  statusCode: number;
}

export interface ResponseDataProvider {
  success: boolean;
  data: DataProvider;
}

export type TDataProvider<T> = {
  paginationData: PaginationData | null;
  rows: Array<T>;
  total: T | null;
};

export interface DataProvider {
  paginationData: PaginationData | null;
  data: any;
  total?: Array<any>;
}

export interface PaginationData {
  currentPage: number;
  pageCount: number;
  perPage: number;
  totalCount: number;
}

export interface ErrorsInterface {
  [name: string]: Array<string>;
}

export interface UserToken {
  token: string;
  expired: string;
}

export interface OptionsFieldInterface {
  label: string;
  value: string;
}

export interface FormDataInterface {
  [name: string]: string | number | boolean | null | Array<string>;
}

export interface ResponseBody {
  data: any;
  status: number;
  statusText: string;
  headers: any;
  config: any;
  request: XMLHttpRequest;
}

export interface ResponseExport {
  download_link: string;
}

export interface RootState {
  version: string;
  loader: boolean;
  sideBarMini: boolean;
  pageTitle: string;
  showHeader: boolean;
  validationErrors: Array<TLocalErrorFieldInput>;
}

export interface CampaignState {
  campaign: CampaignInterface | null;
  campaigns: DataProvider;
  countries: DataProvider;
  ssps: DataProvider;
  langs: DataProvider;
  os: DataProvider;
  category: DataProvider;
  offers: DataProvider;
  deviceType: DataProvider;
  images: DataProvider;
  regions: DataProvider;
  cities: DataProvider;
}

export interface StatisticsState {
  ssp: DataProvider;
  detailSsp: Array<DetailSspStatisticRow>;
  campaign: DataProvider;
  creative: DataProvider;
  lead: DataProvider;
  site: DataProvider;
}

export interface UserInterface {
  id: number | null;
  name: string | null;
  surname: string | null;
  username: string | null;
  phone: string | null;
  email: string | null;
  isConfirmedEmail: boolean | null;
  timezone: number | null;
  language: string | null;
  currency: string | null;
  roles: Array<string>;
  authRule: Array<AuthRule>;
  registerFormIsValid: boolean;
  registerFormStep: number;
  cacheRegisterForm: any;
  timezones: DataProvider;
  users: DataProvider;
}

export interface UserDataInterface {
  id: number | null;
  name: string | null;
  surname: string | null;
  phone: string | null;
  username: string | null;
  email: string | null;
  timezone: number | null;
  language: string | null;
  currency: string | null;
  is_confirmed_email: boolean | null;
  roles: Array<string>;
  authRule: Array<AuthRule>;
}

export interface AuthRule {
  role: string;
  child: Array<string>;
}

export enum userRoles {
  ADMIN = 'admin',
  MANAGER = 'manager',
  SSP = 'ssp',
  PUBLISHER = 'publisher',
}

export enum typeTraffic {
  BANNER = 'banner',
  PUSH = 'push',
  NATIVE = 'native',
}

export enum typeDevice {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export enum statusModeration {
  APPROVE = 'approve',
  REJECT = 'reject',
  NEW = 'new',
}

export enum typeMultiselect {
  INCLUDE = 'include',
  EXCLUDE = 'exclude',
}

export interface FileInterface {
  id: number | null;
  path: string | null;
  type: string | null;
  size: string | null;
  name: string | null;
}

export interface OfferInterface {
  id: number;
  title: string;
  webvork_id: number;
}
export interface DeviceTypeInterface {
  id: number;
  name: string;
  value: number;
}

export interface Country {
  code: string;
  name: string;
}

export interface City {
  id: string;
  name: string;
  region_code?: string;
}

export interface Region {
  code: string;
  name: string;
  country_code?: string;
}

export interface IGeoTarget {
  code: string;
  name: string;
  type?: string;
}

export interface Os {
  id: string;
  name: string;
}

export interface Ssp {
  id: number;
  name: string;
  status: string;
  value: string;
  type_moderation: string;
  moderator?: UserInterface;
  creative_moderation?: CreativeModeration;
  traffic_types?: DropdownList;
  comment?: string | null;
  creative_formats?: any;
  img?: string | null;
  geo?: Array<string> | null;
  rules?: string | null;
}

export interface CreativeModeration {
  id: number | null;
  comment: string;
  created_at: string;
  updated_at: string;
  status: ObjectValue;
}

export interface ModerationCreativeForm {
  id: number | null;
  index: number | null;
  ssp_id: number | null;
  creative_id: number | null;
  name: string;
  comment: string;
  status: ObjectValue | string;
  errors: ErrorsInterface;
}

export interface CampaignInterface {
  id: number | null;
  name: string | null;
  advertiser_domain: string | null;
  full_name: string | null;
  type_traffic: string | null;
  black_site_ids: string | null;
  white_site_ids: string | null;
  is_unique_users: number | null;
  is_use_predictor_ctr: number | null;
  is_use_price_setting: number | null;
  is_limit_budget: number | null;
  is_use_target: number | null;
  is_contextual_target?: number | null;
  contextual_analysis: number | null;
  keywords: string | null;
  target_type: string | null;
  impression: string | null;
  max_cpc: number | string | null;
  max_cpm: number | string | null;
  increase_on: number | string | null;
  limit_type?: string | null;
  limit_goal?: string | null;
  limit_budget?: string | number | null;
  status: ObjectValue | null;
  created_ts: string | null;
  countries?: Array<Country> | null;
  geo_type: string | null;
  geo_values: Array<IGeoTarget> | null;
  screenshot?: FileInterface | null;
  creatives?: Array<Creative>;
  showcases?: Array<IShowcase>;
  device_types?: Array<DeviceTypeInterface>;
  user_targets?: Array<Ssp>;
  is_inventory?: number | string | null;
  ssp_targets?: Array<Ssp>;
  advertising_category: IChipsData;
  languages?: Array<Country>;
  is_device_targets?: number | string | null;
  os?: Array<Os>;
  categories?: Array<IChipsData>;
  user_factor_types?: Array<DropdownList>;
  from_date?: string;
  to_date?: string;
  is_schedule?: number | null;
  schedule?: Array<ScheduleTime>;
  domains?: Array<Domains>;
  audiences?: Array<AudienceStatistics>;
  is_frequency_capping?: number | null;
  strategy_id?: number | null;
  capping_type?: string | null;
  capping_value?: string | null;
  duration_type?: string | null;
  duration_value?: string | null;
  group?: ICampaignGroup | null;
}

export interface CampaignFormInterface {
  id: number | null;
  name: string | null;
  advertiser_domain: string | null;
  full_name: string | null;
  type_traffic: string | null;
  status: ObjectValue | null;
  black_site_ids: string | null;
  white_site_ids: string | null;
  is_unique_users: number | null;
  is_use_predictor_ctr: number | null;
  is_use_price_setting: number | null;
  is_limit_budget: number | null;
  is_use_target: number | null;
  is_contextual_target: number | null;
  contextual_analysis: number | null;
  keywords: string | null;
  target_type: string | null;
  impression: string | null;
  max_cpc: number | string | null;
  max_cpm: number | string | null;
  increase_on: number | string | null;
  limit_type: string | null;
  limit_goal: string | null;
  limit_budget: string | number | null;
  user_targets: Array<string | number>;
  is_inventory: number | null;
  ssp_targets: Array<Ssp>;
  advertising_category: Array<IChipsData>;
  countries?: Array<string | number>;
  geo_type: string;
  geo_values: Array<IDictionaryList>;
  languages: Array<string | number>;
  is_device_targets: number | null;
  os: Array<string | number>;
  device_types: Array<string | number>;
  categories: Array<IChipsData>;
  errors: ErrorsInterface;
  from_date: string;
  to_date: string;
  is_schedule: number | null;
  schedule: Array<ScheduleTime>;
  domains: Array<Domains>;
  changed_domains: Array<Domains>;
  audiences: Array<IChipsData>;
  is_frequency_capping: number | null;
  strategy_id: number | null;
  capping_type: string | null;
  capping_value: string | null;
  duration_type: string | null;
  duration_value: string | null;
  group_id: string | number | null;
}

export interface CampaignFormDataInterface {
  campaign: CampaignFormInterface;
  trafficTypes: Array<DropdownList>;
  userFactorTypes: Array<DropdownList>;
  editCreatives: Array<CreativeWithIndex>;
  selectedCreatives: Array<number>;
  toggleListSites: number;
}

export interface DropdownList {
  label: string | TranslateResult;
  value: string | number;
  img?: string;
}

export interface ObjectValue {
  label: string | TranslateResult;
  value: string;
}

export interface Country {
  countryCode: string | null;
}

export interface CampaignTarget {
  value: string | null;
}

export interface SspStatistic {
  id: number | null;
  name: string | null;
  type_payment: string | null;
  type_auction: string | null;
  from_date: string | null;
  to_date: string | null;
  requests: number | null;
  response: number | null;
  nurl: number | null;
  burl: number | null;
  clicks?: number | null;
  avg_win_cpm?: number | null;
  avg_bidfloor: number | null;
  sum_revenue: number | null;
}

export interface DetailSspStatistic {
  id: number | null;
  name: string | null;
  type_payment: string | null;
  type_auction: string | null;
  date: string | null;
  requests: number | null;
  response: number | null;
  nurl: number | null;
  burl: number | null;
  clicks: number | null;
  avg_win_cpm: number | null;
  avg_bidfloor: number | null;
  sum_revenue: number | null;
}

export interface DetailSspStatisticRow {
  id: number;
  data: Array<DetailSspStatistic>;
}

export interface LeadStatistic {
  created_at: string;
  offer: string;
  country_code: string;
  ssp_id: string;
  ssp_name: string;
  site_id: string;
  site_domain: string;
  campaign_id: string;
  campaign_name: string;
  creative_id: string;
  type_traffic: string;
  device_type: string;
  status: string;
  cr: number;
  cpa: number;
  order_cost: number;
}

export interface CampaignStatistic {
  id: number;
  status?: ObjectValue;
  type_traffic?: ObjectValue;
  name?: string;
  geo?: string;
  count_lead?: number;
  valid?: number;
  confirmed?: number;
  ctr?: number;
  avgCR?: number;
  cpc?: number;
  cpa?: number;
  cpl?: number;
  count_click?: number;
  count_view?: number;
  sum_revenue?: number;
  count_win?: number;
  count_sites?: number;
  avg_revenue?: number;
  avg_bidfloor?: number;
  count_response?: number;
  win_rate?: number;
  roi: number;
  order_cost: number;
}

export interface SiteStatistic {
  site_id: number;
  domain: string;
  avg_revenue?: number;
  ssp_id: number;
  name_ssp: string;
  count_click: number;
  count_lead: number;
  bounce_rate: number;
  sum_revenue: number;
  count_confirm_lead: string;
  cpa: number;
  ctr: number;
  cpc: number;
  count_view: number;
}

export interface CreativeStatistic {
  id: number;
  campaign_id: number;
  campaign: string;
  status: ObjectValue;
  type_traffic?: ObjectValue;
  img: string;
  width: number;
  height: number;
  title: string;
  description: string;
  url?: string;
  landing?: string;
  geo?: string;
  ssp?: string;
  count_click?: number;
  count_view?: number;
  ctr?: number;
  sum_revenue?: number;
  count_lead?: number;
  confirmed?: number;
  cpa?: number;
  cpc?: number;
}

export interface Creative {
  id: number | string | null;
  url: string | null;
  title: string | null;
  description: string | null;
  sponsor?: string | null;
  cta?: string | null;
  cpa_text?: string | null;
  status: ObjectValue | null;
  campaign_id: number | null;
  file_id: number | null;
  img: string | null | ArrayBuffer;
  sourceImg?: any;
  width: number | null;
  height: number | null;
  file: File | null;
  campaign?: CampaignInterface | undefined | null;
  ssp_moderate?: Array<any> | undefined | null;
  statistics?: Array<any> | undefined | null;
  template?: ICreativeTemplate | null;
  isNew?: boolean;
  coordinates?: string | null;
}

export interface CreativeWithIndex {
  index: number | null;
  creative: Creative;
  errors: {};
}
export interface ScheduleTime {
  day_of_week: string;
  times: Array<number | undefined | null>;
}

export interface TabsData {
  name: string;
  tab: string;
  isCheck?: boolean;
  isValid?: boolean;
  isActivated?: boolean;
  buttons: boolean;
}

export interface ICollectionTabsData {
  [name: string]: TabsData;
}

export interface Domains {
  campaign_id: number;
  domain: string;
  coefficient: number;
  budget: number;
  clicks: number;
  status: DomainsStatus;
}

export interface DomainsStatus {
  label: string;
  value: string;
}

export interface IChipsData {
  id?: number;
  parent_id?: number;
  name: string;
  value: string;
  data?: any;
}

export enum ECampaignTabs {
  SETTINGS = 'settings',
  TARGETS = 'targets',
  CREATIVES = 'creatives',
}

export type TFile = {
  name: string;
  data: Blob | null;
};

export type TPayload = {
  id?: string;
  name: string;
  value: any;
};

type TById<T> = {
  [id: string]: T;
};
export interface IStateData<T> {
  byId: TById<T>;
  allIds: Array<string>;
}

export type TSubmitData = {
  APIName: string;
  id?: string;
  backUrl?: string;
};

export enum ERegionType {
  INCLUDE = 'include',
  EXCLUDE = 'exclude',
}

export enum ETargetTypes {
  CPM = 'cpm',
  CPC = 'cpc',
}

export enum EImpressions {
  FIXED_CPM = 'fixed_cpm',
  PERCENTAGE_FLOOR = 'percentage_floor',
}
export type TCollectionIcons = {
  [iconName: string]: string;
};

export type TCollectionInteractiveIcons = {
  [iconName: string]: Function;
};

export type TSize = {
  readonly width: string;
  readonly height: string;
};

export type TLocalErrorFieldInput = {
  id: number;
  errors: Array<string>;
};

export enum ELimitType {
  SPEND = 'spend',
  IMPRESSION = 'impression',
  CLICKS = 'clicks',
}

export enum ELimitGoal {
  TOTAL = 'total',
  DAILY = 'daily',
}

export enum ECappingType {
  IMPRESSIONS = 'impressions',
  CLICKS = 'clicks',
}

export enum EDurationType {
  DAY = 'day',
  CAMPAIGN = 'campaign',
  HOUR = 'hour',
}

export type TRequestData = {
  formData: any;
  id?: number | string;
};

export enum EBudgetType {
  MONETARY_BUDGET = 'monetary_budget',
  IMPRESSIONS = 'impressions',
  CLICKS = 'clicks',
}

export type TModalController = null | {
  resolve: (data?: any) => any;
  reject: (data?: any) => any;
};

export enum EAccountTypes {
  ADVERTISER = 'advertiser',
  PUBLISHER = 'publisher',
}

export interface ISerializeData<T> {
  byId: { [id: string]: T };
  allIds: Array<string>;
}

export enum EStatus {
  ACTIVE = 'active',
  DRAFT = 'draft',
}

export enum ETypeDomains {
  WHITE = 'white',
  BLACK = 'black',
}

export enum EAlignTableCell {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export interface IDataWithLoader<T> {
  loadData: {
    controller: AbortController | null;
    loader: boolean;
  };
  data: T;
}

export type TDropDownItem = {
  readonly label: string;
  readonly value: string | number;
};

export type TDropDownList = Array<TDropDownItem>;

export type TDropDownItemWithType<T> = {
  readonly label: string;
  readonly value: T;
};

export type TDropDownListWithType<T> = Array<TDropDownItemWithType<T>>;

export type TDictionary<T> = { [key: string]: T };

export type TUrlParam = {
  name: string;
  value: string;
};

export type TUrlParams = Array<TUrlParam>;
